import type React from "react"
import { useState, useEffect } from "react"
import {
  Paper,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  Snackbar,
  Stack,
  TextField,
  InputAdornment,
  TablePagination,
  CircularProgress,
  AppBar,
  Toolbar,
  Typography,
} from "@mui/material"
import { Add, Edit, Delete, Search, FileDownload, Refresh } from "@mui/icons-material"
import * as XLSX from "xlsx"
import UserForm from "./UserForm"
import DeleteConfirmationDialog from "./DeleteConfirmationDialog"
import { fetchUsers, addUser, updateUser, deleteUser, type User, type UserFormData } from "../../utils/userAPI"
import { GetTeamLeads } from "../../utils/APIRequest";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { useNavigate } from 'react-router-dom';
import { useTheme } from "@mui/material/styles";

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([])
  const [filteredUsers, setFilteredUsers] = useState<User[]>([])
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [loading, setLoading] = useState(false)

  // Form state
  const [userForm, setUserForm] = useState<{
    open: boolean
    mode: "add" | "edit"
    user: User | null
  }>({
    open: false,
    mode: "add",
    user: null,
  })

  // Team leads list (for dropdown)
  const [teamLeads, setTeamLeads] = useState<User[]>([]);

  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    user: User | null
  }>({
    open: false,
    user: null,
  })

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: "success" | "error"
  }>({
    open: false,
    message: "",
    severity: "success",
  })

  const navigate = useNavigate();
  const theme = useTheme();

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, [])

  // Load team leads from backend for dropdown
  useEffect(() => {
    async function fetchLeads() {
      const leads = await GetTeamLeads();
      setTeamLeads(leads);
    }
    fetchLeads();
  }, []);

  // Filter users based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredUsers(users)
    } else {
      const filtered = users.filter(
        (user) =>
          user.emp_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.username.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      setFilteredUsers(filtered)
    }
    setPage(0)
  }, [users, searchTerm])

  const loadUsers = async () => {
    setLoading(true)
    try {
      const usersData = await fetchUsers();
      const sortedUsers = usersData.sort(
        (a: User, b: User) => Number(a.username) - Number(b.username)
      );
      setUsers(sortedUsers);
    } catch (error) {
      console.error("Error loading users:", error)
      setSnackbar({
        open: true,
        message: "Error loading users. Please try again.",
        severity: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleAddUser = () => {
    setUserForm({
      open: true,
      mode: "add",
      user: null,
    })
  }

  const handleEditUser = (user: User) => {
    setUserForm({
      open: true,
      mode: "edit",
      user,
    })
  }

  const handleDeleteUser = (user: User) => {
    setDeleteDialog({
      open: true,
      user,
    })
  }

  const handleUserFormSubmit = async (userData: UserFormData) => {
    try {
      if (userForm.mode === "add") {
        await addUser({ ...userData, teamLead: userData.teamLead });
        setSnackbar({
          open: true,
          message: "User added successfully",
          severity: "success",
        });
      } else if (userForm.mode === "edit" && userForm.user) {
        await updateUser(userForm.user.username, {
          emp_name: userData.emp_name,
          password: userData.password,
          isAdmin: userData.isAdmin,
          designation: userData.designation,
          teamLead: userData.teamLead,
        });
        setSnackbar({
          open: true,
          message: "User updated successfully",
          severity: "success",
        });
      }

      loadUsers()
      setUserForm({ open: false, mode: "add", user: null })
    } catch (error) {
      console.error("Error saving user:", error)
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : "An error occurred",
        severity: "error",
      })
    }
  }

  const handleConfirmDelete = async () => {
    if (deleteDialog.user) {
      try {
        const message = await deleteUser(deleteDialog.user.username)
        setSnackbar({
          open: true,
          message: message,
          severity: "success",
        })
        loadUsers()
      } catch (error) {
        console.error("Error deleting user:", error)
        setSnackbar({
          open: true,
          message: error instanceof Error ? error.message : "Error deleting user",
          severity: "error",
        })
      }
    }
    setDeleteDialog({ open: false, user: null })
  }

  const handleExportUsers = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredUsers.map((user) => ({
        Username: user.username,
        "Employee Name": user.emp_name,
        "Designation": getDesignationLabel(user.designation) || '-',
        "Team Lead": user.teamLead ? users.find(u => u.username === user.teamLead)?.emp_name || user.teamLead : "-",
        "Role": user.isAdmin ? "Admin" : "Employee",
      })),
    )

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users")
    XLSX.writeFile(workbook, `users_${new Date().toISOString().split("T")[0]}.xlsx`)
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  // Calculate pagination
  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const getDesignationLabel = (value: string | undefined) => {
    const designationOptions = [
      { value: "developer", label: "Software Developer" },
      { value: "senior_developer", label: "Senior Software Developer" },
      { value: "asso_team_lead", label: "Associate Team Lead" },
      { value: "team_lead", label: "Team Lead" },
      { value: "ba", label: "Business Analyst" },
      { value: "qa", label: "Quality Analyst" },
      { value: "project_manager", label: "Project Manager" },
      { value: "hr", label: "HR" },
    ];
    const found = designationOptions.find(opt => opt.value === value);
    return found ? found.label : '-';
  };

  return (
    <Box>
      {/* Header */}
      <AppBar position="fixed" elevation={0} sx={{ background: `linear-gradient(135deg,${theme.palette.primary.main},${theme.palette.primary.dark})` }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate("/")}>
            <ArrowBackIcon />
          </IconButton>
          <ManageAccountsIcon sx={{ mr: 2, color: theme.palette.secondary.contrastText }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600, color: theme.palette.secondary.contrastText }}>
            User Management
          </Typography>
        </Toolbar>
      </AppBar>
      <Box sx={{ pt: 2, display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        {/* Removed User Management Typography here */}
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search users by username or employee name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Summary Stats and Actions */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={3}>
          <Chip label={`Total Users: ${users.length}`} color="primary" variant="outlined" />
          <Chip label={`Admins: ${users.filter((u) => u.isAdmin).length}`} color="secondary" variant="outlined" />
          <Chip label={`Employees: ${users.filter((u) => !u.isAdmin).length}`} color="default" variant="outlined" />
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadUsers} disabled={loading}>
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={handleExportUsers}
            disabled={filteredUsers.length === 0}
          >
            Export Users
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={handleAddUser}>
            Add User
          </Button>
        </Stack>
      </Box>

      {/* Users Table */}
      <TableContainer component={Paper} elevation={2}>
        <Table sx={{ minWidth: 1200 }} aria-label="users table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.light" }}>
              <TableCell sx={{ color: "white" }}>Username</TableCell>
              <TableCell sx={{ color: "white" }}>Employee Name</TableCell>
              <TableCell sx={{ color: "white" }}>Designation</TableCell>
              <TableCell sx={{ color: "white" }}>Team Lead</TableCell>
              <TableCell sx={{ color: "white" }}>Role</TableCell>
              <TableCell sx={{ color: "white" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Loading users...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paginatedUsers.length > 0 ? (
              paginatedUsers.map((user) => (
                <TableRow key={user._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {user.username}
                    </Typography>
                  </TableCell>
                  <TableCell>{user.emp_name}</TableCell>
                  <TableCell>{getDesignationLabel(user.designation) || '-'}</TableCell>
                  <TableCell>
                    {user.teamLead ? (
                      <Typography variant="body2" color="text.secondary">
                        {users.find(u => u.username === user.teamLead)?.emp_name || user.teamLead}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.isAdmin ? "Admin" : "Employee"}
                      color={user.isAdmin ? "primary" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Edit User">
                        <IconButton color="primary" onClick={() => handleEditUser(user)} size="small">
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete User">
                        <IconButton color="error" onClick={() => handleDeleteUser(user)} size="small">
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {searchTerm ? "No users found matching your search" : "No users found"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredUsers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ mb: 4 }}
      />

      {/* User Form Dialog */}
      <UserForm
        open={userForm.open}
        onClose={() => setUserForm({ open: false, mode: "add", user: null })}
        onSubmit={handleUserFormSubmit}
        user={userForm.user}
        mode={userForm.mode}
        teamLeads={teamLeads}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, user: null })}
        onConfirm={handleConfirmDelete}
        title="Delete User"
        message={`Are you sure you want to delete user "${deleteDialog.user?.emp_name}" (${deleteDialog.user?.username})? This action cannot be undone and will also remove all their poll responses.`}
        itemCount={1}
      />

      {/* Snackbar for notifications */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default UserManagement
